import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    FormBuilder,
    FormGroup,
    FormArray,
    Validators,
    ReactiveFormsModule,
    Validator,
    AbstractControl,
    ValidationErrors,
    NG_VALIDATORS
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CreateCatastrophicMedicalAgePricingDto, CurrencyEntryDto } from '../../../../core/data/new-dtos/insurance-type.dtos';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
    selector: 'app-catastrophic-medical-age-pricing-selector',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        NgxMaskDirective,
        NgbTooltipModule,
        NgSelectModule
    ],
    templateUrl: './catastrophic-medical-age-pricing-selector.component.html',
    styleUrl: './catastrophic-medical-age-pricing-selector.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CatastrophicMedicalAgePricingSelectorComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CatastrophicMedicalAgePricingSelectorComponent),
            multi: true,
        },
        provideNgxMask(),
    ],
})
export class CatastrophicMedicalAgePricingSelectorComponent implements ControlValueAccessor, Validator, OnChanges {
    @Input() allowsFamily = false;
    @Input() currencies: CurrencyEntryDto[] = [];

    form: FormGroup;

    private isWriteValue = false;
    private cachedValue: any = null;

    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({
            pricings: this.fb.array([]),
            childPricings: this.fb.array([]),
        });

        this.form.valueChanges.subscribe((val) => {
            if (!this.isWriteValue) {
                const results: any[] = [];

                // Combine both arrays
                const combined = [...(val.pricings || [])];
                if (this.allowsFamily) {
                    combined.push(...(val.childPricings || []));
                }

                // Ungroup the rows
                combined.forEach(row => {
                    const genders = row.gender as string[];
                    const premiums = row.premiums; // Object Map { [currencyId]: amount }

                    if (genders && genders.length > 0 && premiums) {
                        genders.forEach(g => {
                            Object.keys(premiums).forEach(currencyIdStr => {
                                results.push({
                                    personInsuranceCurrencyId: Number(currencyIdStr),
                                    minAge: row.minAge,
                                    maxAge: row.maxAge,
                                    gender: g,
                                    isForChild: row.isForChild,
                                    premiumAmount: premiums[currencyIdStr]
                                });
                            });
                        });
                    }
                });

                this.onChange(results);
                this.onTouched();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['currencies']) {
            // Currencies have loaded or changed. Re-render if we have a cached value.
            if (this.cachedValue) {
                this.writeValue(this.cachedValue);
            }
        }
    }

    get pricings(): FormArray {
        return this.form.get('pricings') as FormArray;
    }

    get childPricings(): FormArray {
        return this.form.get('childPricings') as FormArray;
    }

    createPricingGroup(pricing?: any): FormGroup {
        const group = this.fb.group({
            minAge: [pricing?.minAge || 0, [Validators.required, Validators.min(0)]],
            maxAge: [pricing?.maxAge || 0, [Validators.required, Validators.min(0)]],
            gender: [pricing?.gender ? pricing.gender : ['M', 'F'], [Validators.required]],
            isForChild: [pricing?.isForChild || false],
            premiums: this.fb.group({})
        });

        const premiumsGroup = group.get('premiums') as FormGroup;
        this.currencies.forEach(c => {
            const val = pricing?.premiums ? pricing.premiums[c.id.toString()] : 0;
            premiumsGroup.addControl(c.id.toString(), this.fb.control(val, [Validators.required, Validators.min(0)]));
        });

        return group;
    }

    addPricing(isChild: boolean = false) {
        const pricing = this.createPricingGroup({ isForChild: isChild } as CreateCatastrophicMedicalAgePricingDto);
        if (isChild && this.allowsFamily) {
            this.childPricings.push(pricing);
        } else {
            this.pricings.push(pricing);
        }
    }

    removePricing(index: number, isChild: boolean = false) {
        if (isChild && this.allowsFamily) {
            this.childPricings.removeAt(index);
        } else {
            this.pricings.removeAt(index);
        }
    }

    writeValue(obj: any): void {
        if (obj && Array.isArray(obj)) {
            this.cachedValue = obj; // Cache for asynchronously loaded currencies

            if (!this.currencies || this.currencies.length === 0) {
                // Cannot render form groups without currencies yet! Wait for ngOnChanges.
                return;
            }

            // Wait a tick to prevent an infinite reactive loop during init
            setTimeout(() => {
                this.isWriteValue = true;
                this.pricings.clear();
                this.childPricings.clear();

                // Step 1: Group by logical row (Ages + Child Flag + Single Gender) to accumulate all currency premiums
                const rulesByGender = new Map<string, any>();
                obj.forEach((pricing: any) => {
                    const ruleKey = `${pricing.minAge}-${pricing.maxAge}-${pricing.isForChild}-${pricing.gender}`;
                    if (!rulesByGender.has(ruleKey)) {
                        rulesByGender.set(ruleKey, {
                            minAge: pricing.minAge,
                            maxAge: pricing.maxAge,
                            isForChild: pricing.isForChild,
                            gender: pricing.gender,
                            premiums: {} as any
                        });
                    }
                    if (pricing.personInsuranceCurrencyId) {
                        rulesByGender.get(ruleKey).premiums[pricing.personInsuranceCurrencyId.toString()] = pricing.premiumAmount;
                    }
                });

                // Step 2: Group logical rules into UI rows ONLY if they share the exact same premiums
                const uiRows = new Map<string, any>();
                rulesByGender.forEach((rule) => {
                    // Create a signature based on ages, child flag, and a sorted JSON string of premiums
                    const premiumKeys = Object.keys(rule.premiums).sort();
                    const premiumSignature = premiumKeys.map(k => `${k}:${rule.premiums[k]}`).join('|');
                    const uiRowKey = `${rule.minAge}-${rule.maxAge}-${rule.isForChild}-${premiumSignature}`;

                    if (uiRows.has(uiRowKey)) {
                        const existing = uiRows.get(uiRowKey);
                        if (!existing.gender.includes(rule.gender)) {
                            existing.gender.push(rule.gender);
                        }
                    } else {
                        uiRows.set(uiRowKey, {
                            ...rule,
                            gender: [rule.gender]
                        });
                    }
                });

                // Step 3: Populate Form Arrays
                uiRows.forEach((pricing) => {
                    if (pricing.isForChild && this.allowsFamily) {
                        this.childPricings.push(this.createPricingGroup(pricing));
                    } else {
                        // If family is not allowed, we force isForChild to false just in case
                        if (!this.allowsFamily) {
                            pricing.isForChild = false;
                        }
                        this.pricings.push(this.createPricingGroup(pricing));
                    }
                });
                this.isWriteValue = false;
            });
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.form.valid ? null : { invalidForm: { valid: false, message: 'Tarifas incompletas' } };
    }

    markAllAsTouched() {
        this.form.markAllAsTouched();
    }

    private onChange: any = () => { };
    private onTouched: any = () => { };
}
